import type { ButtonHTMLAttributes, FunctionComponent, MouseEvent } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './Button.module.css';

export enum ButtonVariant {
  MAIN = 'main',
  ACCENT = 'accent',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: ButtonVariant;
  badge?: string | number;
  disabled?: boolean;
  loading?: boolean;
  tk?: string;
}

export const Button: FunctionComponent<ButtonProps> = ({
  badge,
  children,
  className,
  disabled = false,
  loading = false,
  onClick,
  tk,
  variant,
  ...rest
}) => {
  const audioTrigger = useAudioTrigger();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: 'menuClick' });
    onClick?.(event);
  };

  const buttonMods = useModifiers(
    styles,
    'button',
    {
      accent: variant === ButtonVariant.ACCENT,
      main: variant === ButtonVariant.MAIN,
    },
    { withBaseClass: true, extraClassName: className }
  );

  return (
    <button
      data-testid="button"
      aria-busy={loading || undefined}
      className={buttonMods}
      disabled={disabled || loading}
      onClick={handleClick}
      {...rest}
    >
      {loading ? (
        <svg className={styles.loading} viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="11" strokeWidth="2" strokeDasharray="28" strokeLinecap="round" />
        </svg>
      ) : null}
      <span data-testid="button-content" className={styles.content}>
        {tk ? <L10nText tk={tk} /> : children}
      </span>
      {badge ? (
        <span data-testid="badge" className={styles.badge}>
          {badge}
        </span>
      ) : null}
    </button>
  );
};
